import api from '../../utils/API';

// Client for the GoMail (mailtalent) API. Auth is the Microsoft session cookie set by the API,
// so every call is sent with credentials.
// Same rule as the CRM API (utils/API.js): local mailtalent on localhost, otherwise the
// /mailtalent path of the current domain (dev.goandev.info in dev, the CRM domain in prod).
// REACT_APP_GOMAIL_API_URL overrides it when set.
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]'];

const resolveApiUrl = () => {
  if (process.env.REACT_APP_GOMAIL_API_URL) return process.env.REACT_APP_GOMAIL_API_URL;
  if (LOCAL_HOSTS.includes(window.location.hostname)) return 'http://localhost:8005/mailtalent';
  return `${window.location.origin}/mailtalent`;
};

export const GOMAIL_API_URL = resolveApiUrl().replace(/\/$/, '');

export class GoMailApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'GoMailApiError';
    this.status = status;
  }
}

const buildUrl = (path, params) => {
  let url = `${GOMAIL_API_URL}${path}`;
  if (params) {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) sp.set(k, String(v));
    });
    const qs = sp.toString();
    if (qs) url += `${url.includes('?') ? '&' : '?'}${qs}`;
  }
  return url;
};

export async function gomailFetch(path, { params, body, method = 'GET', ...rest } = {}) {
  const res = await fetch(buildUrl(path, params), {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...rest,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let message = text || `HTTP ${res.status}`;
    try {
      const json = JSON.parse(text);
      if (json?.detail) message = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
    } catch (e) {
      // not JSON, keep raw text
    }
    throw new GoMailApiError(message, res.status);
  }
  const type = res.headers.get('content-type') || '';
  return type.includes('application/json') ? res.json() : null;
}

export async function gomailFetchBlob(path) {
  const res = await fetch(buildUrl(path), { credentials: 'include' });
  if (!res.ok) throw new GoMailApiError(`HTTP ${res.status}`, res.status);
  return res.blob();
}

export const gomailLoginUrl = () => `${GOMAIL_API_URL}/auth/login`;

// The candidate is created through this CRM's own API (same endpoint as the browser extension),
// so it always lands in the environment the user is working in (local, dev or prod) and is
// attributed to the logged-in CRM user.
export async function createCrmCandidate(form) {
  const { data } = await api.post('/extension/add', {
    candidatOrContact: 'candidat',
    linkedin: form.linkedin,
    prenom: form.prenom,
    nom: form.nom,
    fonction: form.fonction,
    employeur: form.employeur,
    ecole: form.ecole,
    localisation: form.localisation,
    email: form.email,
    telephone: form.telephone,
    competences: form.competences,
    // The CRM parses this as a date: a bare year ("2026") would be read as a time of day.
    dateDebut: /^\d{4}$/.test(String(form.date_debut || '').trim()) ? `${String(form.date_debut).trim()}-01-01` : form.date_debut || undefined,
    viviers: [],
  });
  const id = data?.candidat_id || data?.id;
  if (!id) throw new Error(data?.message || "Le CRM n'a pas renvoyé l'identifiant du candidat.");
  return { id, path: `/candidats/${id}/view`, updated: data?.etat === 'updated' };
}
